<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useVideoStore } from '@/stores';
import { useQuasar } from 'quasar';
import { PageHeader } from '@/components/clients/common';
import { DashPlayer } from '@/components/clients/gallery';
import { useEventStore } from '@/stores/event/event.store';

const eventStore = useEventStore();
const videoStore = useVideoStore();
const quasar = useQuasar();

const mainColor = eventStore.getColors.mainColor;
const backgroundColor = eventStore.getColors.backgroundColor;
const clientId = eventStore.getColors.clientId;

const numberOfColumns = ref(0);
const columnId = ref(0);

watch(
  () => videoStore.numberOfVideos,
  () => {
    columnId.value++;
  },
);

function checkScreen() {
  numberOfColumns.value = parseInt(window.innerWidth / 250);
  columnId.value++;
}

function getElements(n) {
  return videoStore.getElements(n, numberOfColumns.value);
}

async function getVideo() {
  const numAddedVideos = await videoStore.loadVideos(clientId);

  if (numAddedVideos !== 0) {
    window.addEventListener('scroll', handleScroll);
    quasar.notify({
      type: 'positive',
      message: `Załadowano pliki.`,
    });
  } else {
    quasar.notify({
      type: 'negative',
      message: `Brak nowych video do załadowania.`,
    });
  }
}

async function handleScroll() {
  const scrollPositionFromBottom =
    document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
  const distanceFromBottomToTriggerAction = 150;

  if (scrollPositionFromBottom <= distanceFromBottomToTriggerAction) {
    const numAddedVideos = await videoStore.loadVideos(clientId);
    if (numAddedVideos === 0) {
      window.removeEventListener('scroll', handleScroll);
    }
  }
}

onMounted(async () => {
  window.addEventListener('resize', checkScreen);
  checkScreen();

  await videoStore.loadVideos(clientId);
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreen);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <div class="background">
    <PageHeader
      :main-color="mainColor"
      :background-color="backgroundColor"
      header-text="Galeria Wideo"
    />
    <div class="gallery">
      <div v-for="n in numberOfColumns" :key="n" class="column">
        <div v-for="(video, i) in getElements(n - 1)" :key="n * i" class="box">
          <DashPlayer :manifest-url="video.url" />
        </div>
      </div>
    </div>
    <div class="bottom">
      <div v-if="videoStore.isLoading" class="loading-dots">
        <span class="dot dot1"></span>
        <span class="dot dot2"></span>
        <span class="dot dot3"></span>
      </div>
      <button v-else class="button more" @click="getVideo">
        Więcej filmów
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.container {
  margin: 50px auto;
  max-width: 1500px;
}

.button {
  border-radius: 48.707px;
  border: 2px solid v-bind(mainColor);
  color: v-bind(mainColor);
  padding: 15px 25px;
  font-size: 25px;
  white-space: nowrap;
  text-align: center;
  text-transform: uppercase;
  transition: background-color 1s ease;

  &:hover {
    background-color: v-bind(mainColor);
    color: v-bind(backgroundColor);
  }

  @media screen and (max-width: 500px) {
    font-size: 18px;
    line-height: 30px;
    padding: 10px 20px;
  }
}

.bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2% 0 3%;
}

.gallery {
  position: relative;
  padding: 2% 3%;
  padding-top: 125px;
  width: auto;
  height: auto;
  display: grid;
  grid-template-columns: repeat(v-bind(numberofcolumns), 1fr);
  gap: 5px;
  place-items: center;

  .column {
    display: flex;
    gap: 5px;
    height: 100%;
  }

  .box {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.expanded {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100vw;
  height: 100vh;
  background: rgb(0 0 0 / 70%);
  display: flex;
  align-items: center;

  .overlay {
    width: 100vw;
    height: 100vh;
    z-index: 9;
  }

  .right-space,
  .left-button,
  .right-button {
    border: none;
    height: 80px;
    width: 100%;
    padding: 0;
  }

  .left-button,
  .right-button {
    color: white;
    font-size: 32px;

    &:hover {
      background-color: rgb(255 255 255 / 10%);
      color: white;
    }

    @media screen and (max-width: 768px) {
      &:hover {
        background-color: rgb(0 0 0 / 0%);
      }
    }
  }

  .right-button-next {
    margin-top: auto;
    margin-bottom: auto;
  }

  .left {
    margin-left: 4%;
    width: 100px;

    @media screen and (max-width: 600px) {
      margin-left: 5px;
      width: 50px;
    }
  }

  .right {
    margin-right: 4%;
    width: 100px;
    height: 95%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: start;

    @media screen and (max-width: 600px) {
      margin-right: 5px;
      width: 50px;
    }
  }
}

.movie {
  max-width: 100%;
  max-height: 75vh;
  cursor: pointer;
}

.movie-expanded {
  position: fixed;
  width: auto;
  height: auto;
  display: block;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.7);
  z-index: 11;
}

.background {
  background-color: v-bind(backgroundColor);
  min-height: 100vh;
}

.loading-dots {
  text-align: center;
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.dot {
  display: inline-block;
  width: 17px;
  height: 17px;
  background-color: v-bind(mainColor);
  border-radius: 50%;
  margin: 0 4px;
  animation: bounce 2s infinite alternate;
}

.dot1 {
  animation-duration: 1s;
}

.dot2 {
  animation-duration: 1.5s;
}

.dot3 {
  animation-duration: 2s;
}

@keyframes bounce {
  0% {
    transform: translateY(0);
  }

  100% {
    transform: translateY(-16px);
  }
}
</style>
